/**
 * The Board's approval chain, shown as a ladder on the document it belongs to.
 *
 * The status pill says SUBMITTED for as long as the chain is running, which is
 * correct — the document is not approved until the last signature — but on its
 * own it tells a Project Manager nothing about whether he is holding it up.
 * This does.
 *
 * Documents raised before the Board resolution have no chain and render
 * nothing at all.
 */
import { useEffect, useState } from 'react'
import { CheckCircle2, Circle, Clock, RotateCcw, XCircle } from 'lucide-react'
import { APPROVAL_CHAIN, ROLE_LABEL } from '@/lib/domain'
import { usePeople } from '@/lib/data'
import { supabase } from '@/lib/supabase'
import { dateTime } from '@/lib/format'

interface StepRow {
  step_no: number
  action: string | null
  actor_email: string | null
  actor_role: string | null
  is_standin: boolean | null
  comment: string | null
  acted_at: string | null
}

export function ApprovalTrail({ docType, docId, chainVersion, currentStep, status }: {
  docType: 'PR' | 'PO'
  docId: string
  chainVersion?: number | null
  currentStep?: number | null
  status: string
}) {
  const [rows, setRows] = useState<StepRow[]>([])
  const chained = (chainVersion ?? 1) >= 2
  // The ladder names the person who signed, not their email address.
  // document_approvals records only the email, so the lookup resolves on that.
  const people = usePeople()

  useEffect(() => {
    if (!chained) { setRows([]); return }
    let alive = true
    void (async () => {
      const { data } = await supabase
        .from('document_approvals')
        .select('step_no, action, actor_email, actor_role, is_standin, comment, acted_at')
        .eq('doc_type', docType).eq('doc_id', docId)
        .order('acted_at', { ascending: true })
      if (alive) setRows((data ?? []) as StepRow[])
    })()
    return () => { alive = false }
  }, [docType, docId, chained])

  if (!chained) return null

  const steps = APPROVAL_CHAIN[docType]
  const done = new Map<number, StepRow>()
  // Latest action per step wins — a returned document that comes back round
  // overwrites what happened on the previous pass.
  rows.forEach((r) => done.set(r.step_no, r))

  const cleared = steps.filter((s) => done.get(s.step)?.action === 'APPROVE').length

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="mb-2 flex items-baseline justify-between">
        <p className="text-xs font-semibold text-slate-500">Approval chain</p>
        <p className="text-xs text-slate-500">
          {status === 'APPROVED' ? 'All signatures given'
            : status === 'REJECTED' ? 'Stopped'
              : status === 'RETURNED' ? 'Back with the originator'
                : `${cleared} of ${steps.length} cleared`}
        </p>
      </div>

      <ol className="space-y-1.5">
        {steps.map((s) => {
          const r = done.get(s.step)
          const waiting = status === 'SUBMITTED' && currentStep === s.step
          const approved = r?.action === 'APPROVE'
          const rejected = r?.action === 'REJECT'
          const returned = r?.action === 'RETURN'

          const Icon = approved ? CheckCircle2 : rejected ? XCircle
            : returned ? RotateCcw : waiting ? Clock : Circle
          const tone = approved ? 'text-emerald-600' : rejected ? 'text-red-600'
            : returned ? 'text-amber-600' : waiting ? 'text-blue-600' : 'text-slate-300'

          return (
            <li key={s.step} className="flex gap-2.5">
              <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${tone}`} />
              <div className="min-w-0 flex-1">
                <p className={`text-sm ${waiting ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>
                  <span className="text-xs text-slate-400">{s.step}.</span> {s.label}
                  {waiting && <span className="ml-2 pill-amber">Waiting here now</span>}
                  {r?.is_standin && <span className="ml-2 pill-violet">Stand-in</span>}
                </p>

                {r?.acted_at && (
                  <p className="text-xs text-slate-500">
                    {approved ? 'Cleared by' : rejected ? 'Rejected by' : 'Sent back by'}{' '}
                    {people.name({ email: r.actor_email, role: r.actor_role })}
                    {r.actor_role ? ` (${ROLE_LABEL[r.actor_role as keyof typeof ROLE_LABEL] ?? r.actor_role})` : ''}
                    {' · '}{dateTime(r.acted_at)}
                  </p>
                )}
                {!r?.acted_at && waiting && (
                  <p className="text-xs text-slate-500">{s.hint}</p>
                )}
                {r?.comment && (
                  <p className="mt-0.5 rounded bg-slate-50 px-2 py-1 text-xs text-slate-700">{r.comment}</p>
                )}
              </div>
            </li>
          )
        })}
      </ol>

      {status === 'RETURNED' && (
        <p className="mt-2 text-xs text-amber-700">
          When this is corrected and resubmitted the chain starts again at stage 1. A revised
          document is a different commitment, and everyone who cleared the old figures has to
          see the new ones.
        </p>
      )}
    </div>
  )
}
